import { X } from "lucide-react";
import { useCallback, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../ui/components/ui/form";
import { Input } from "../../../ui/components/ui/input";

import { OnboardingFormData } from "../onboarding-schema";

const SUGGESTED_BENEFITS = [
  "Dienstwagen",
  "Wunschdienstplan",
  "Fortbildungen & Weiterbildungen",
  "Übertarifliche Zuschläge",
  "Team-Events",
  "Bonuszahlungen",
  "Betriebliche Altersvorsorge",
  "Familienfreundliche Arbeitszeiten",
  "Mitarbeiterrabatte",
  "Kostenlose Getränke & Snacks",
  "Homeoffice-Möglichkeit",
  "JobRad / Fahrtkostenzuschuss",
];

interface BenefitsStepProps {
  form: UseFormReturn<OnboardingFormData>;
}

const BenefitsStep = ({ form }: BenefitsStepProps) => {
  const [inputValue, setInputValue] = useState("");

  const benefits = form.watch("benefits") ?? [];

  const addBenefit = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) return;
      const current = form.getValues("benefits") ?? [];
      if (current.includes(trimmed)) return;
      form.setValue("benefits", [...current, trimmed], { shouldValidate: true });
    },
    [form],
  );

  const removeBenefit = useCallback(
    (value: string) => {
      const current = form.getValues("benefits") ?? [];
      form.setValue(
        "benefits",
        current.filter((b) => b !== value),
        { shouldValidate: true },
      );
    },
    [form],
  );

  const handleAdd = () => {
    addBenefit(inputValue);
    setInputValue("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  const availableSuggestions = SUGGESTED_BENEFITS.filter((s) => !benefits.includes(s));

  return (
    <div>
      <p className="text-sm font-medium mb-1" style={{ color: "#0099ce" }}>
        Schritt 5 von 9
      </p>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Welche Vorteile bieten Sie Ihren Mitarbeitern?</h1>
      <p className="text-sm text-gray-500 mb-6">
        Benefits sind ein entscheidender Faktor bei der Wahl des Arbeitgebers und machen Ihre Stellen deutlich
        attraktiver.
      </p>

      <div className="space-y-6">
        {/* Section 1: Benefits */}
        <div className="rounded-xl bg-white shadow-sm border border-gray-100 p-8">
          <h2 className="text-base font-semibold text-gray-900 mb-1">Liste aller Mitarbeiter-Benefits</h2>
          <p className="text-sm text-gray-500 mb-6">
            Viele Unternehmen unterschätzen, wie viele Vorteile sie tatsächlich bieten. Diese Angaben machen Ihre
            Stellen attraktiver und sind entscheidend im Wettbewerb um Pflege- und Gesundheitskräfte.
          </p>

          <FormField
            control={form.control}
            name="benefits"
            render={() => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">Mitarbeiter-Benefits</FormLabel>

                <FormControl>
                  <div className="mt-1">
                    <div className="flex gap-2">
                      <Input
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Eigenen Benefit hinzufügen"
                        className="flex-1"
                      />
                      <button
                        type="button"
                        onClick={handleAdd}
                        disabled={!inputValue.trim()}
                        className="px-4 py-2 text-sm font-medium text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ backgroundColor: "#0099ce" }}
                      >
                        Hinzufügen
                      </button>
                    </div>
                  </div>
                </FormControl>

                {benefits.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {benefits.map((benefit) => (
                      <span
                        key={benefit}
                        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium text-white"
                        style={{ backgroundColor: "#0099ce" }}
                      >
                        {benefit}
                        <button
                          type="button"
                          onClick={() => removeBenefit(benefit)}
                          className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {availableSuggestions.length > 0 && (
                  <div className="mt-5">
                    <p className="text-sm font-medium text-gray-600 mb-2">Häufig angebotene Benefits:</p>
                    <div className="flex flex-wrap gap-2">
                      {availableSuggestions.map((suggestion) => (
                        <button
                          key={suggestion}
                          type="button"
                          onClick={() => addBenefit(suggestion)}
                          className="rounded-full px-3 py-1.5 text-sm font-medium border border-gray-200 bg-white text-gray-600 hover:border-gray-400 transition-colors"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default BenefitsStep;
